import { MiteneFormattedMessage } from 'components/atoms/MiteneFormattedMessage'
import { type FC, useState } from 'react'

import { Button } from 'components/design-system/primitives/Button'
import { Heading } from 'components/design-system/primitives/Typography/Heading'
import { Text } from 'components/design-system/primitives/Typography/Text'
import { PlanProps, PlanSelection } from 'components/organisms/premium/common/PlanSelection'
import { subscriptionTypePremium, subscriptionTypePremiumPro } from 'constants/common'
import { navigationToPremiumPlansAlbums, PLAN_SELECTION_FROM } from 'utils/navigation'
import { useIntl } from 'react-intl'

export interface PlanSectionProps {
  premiumPlan: PlanProps[]
  proPlan: PlanProps[]
  onClick: (priceId: string) => void
  onBack?: () => void
  defaultPriceId?: string
}

export const PremiumPlans: FC<{
  availablePlans: {
    subscriptionType: string
    name: string
    priceId: string
    price: string
    interval: string
    appProductId: string
    discountedMonthlyPrice?: string
  }[]
  campaignEnabled: boolean,
}> = props => {
  const intl = useIntl()
  const planSelectionProps: PlanSectionProps = {
    premiumPlan: props.availablePlans
      .filter(plan => plan.subscriptionType === subscriptionTypePremium)
      .map(plan => ({
        name: plan.name,
        price: plan.price,
        priceId: plan.priceId,
        interval: plan.interval,
        appProductId: plan.appProductId,
        discountedMonthlyPrice: plan.discountedMonthlyPrice,
      })),
    proPlan: props.availablePlans
      .filter(plan => plan.subscriptionType === subscriptionTypePremiumPro)
      .map(plan => ({
        name: plan.name,
        price: plan.price,
        priceId: plan.priceId,
        interval: plan.interval,
        appProductId: plan.appProductId,
        discountedMonthlyPrice: plan.discountedMonthlyPrice,
      })),
    onClick: (priceId: string) => {
      const m = new Map<string, string>()
      m.set('price_id', priceId)
      m.set('from', PLAN_SELECTION_FROM)
      navigationToPremiumPlansAlbums(m)
    },
    onBack: () => {
      window.history.back()
    },
  }

  // デフォルトのプランを取得（初期表示用）- intervalを使用
  const defaultPlan =
    planSelectionProps.proPlan.find(plan => plan.interval === 'year') ||
    planSelectionProps.proPlan[0]
  const defaultPriceId = planSelectionProps.defaultPriceId || defaultPlan?.priceId || ''

  // 選択中のプランIDを管理するステートを追加
  const [selectedPriceId, setSelectedPriceId] = useState<string>(defaultPriceId)

  // プラン選択時の処理（状態の更新のみ）
  const handleSelectPlan = (priceId: string) => {
    setSelectedPriceId(priceId)
  }

  // 「このプランで次へ」ボタンクリック時の処理
  const handleNextClick = () => {
    if (selectedPriceId) {
      planSelectionProps.onClick(selectedPriceId)
    }
  }

  return (
    <div className='w-full h-full bg-white'>
      <div className='w-full max-w-lg mx-auto px-4'>
        <div className='text-center mb-6 pt-6'>
          <Heading level={2}>
            <MiteneFormattedMessage id='premiumPlan.title' />
          </Heading>
        </div>

        <div className='mb-10'>
          <PlanSelection
            onSelectPlan={handleSelectPlan}
            selectedPriceId={selectedPriceId}
            premiumPlan={planSelectionProps.premiumPlan}
            proPlan={planSelectionProps.proPlan}
          />
        </div>

        <div className='flex flex-col items-center gap-8 pb-8'>
          <div className='flex flex-col items-center w-full'>
            {props.campaignEnabled ? (
              <Text color='accent-secondary' className='font-semibold mb-2'>
                <MiteneFormattedMessage id='premiumPlan.campaignAppealText' />
              </Text>
            ) : intl.locale === 'ja' && (
              /* 日本円以外では特にお得ではないので、日本語の場合のみ表示する */
              <Text color='accent-secondary' className='font-semibold mb-2'>
                <MiteneFormattedMessage id='premiumPlan.defaultAppealText' values={{ number: 10 }} />
              </Text>
            )}
            <Button variant='primary' fullWidth={true} onClick={handleNextClick}>
              <MiteneFormattedMessage id='premiumPlan.confirmButton' />
            </Button>
          </div>
          {planSelectionProps.onBack && (
            <Button variant='link' onClick={planSelectionProps.onBack}>
              <MiteneFormattedMessage id='premiumPlan.backButton' />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
